import { useState, useEffect, useMemo, useCallback } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { Dropdownlist } from '@/components/Dropdownlist';
import { DateTimePicker } from '@/components/DateTimePicker';
import { navigateToNewCitation, navigateToCitation } from '@/services/navigation';
import { App } from '@/services/app-compat';
import { api } from '@/services/api-class';
import moment from 'moment';
import { getModuleType, currentUserHasPermission, getOfficerLabel } from '@/utils/legacy-utils';
import { AccessAuthorities } from '@/models';

// ============================================================================
// External Declarations
// ============================================================================

import { getOfficerPluralLabel } from '@/utils/auth';

let citationParentContext = (window as any).citationParentContext;
let citationParentId = (window as any).citationParentId;

// ============================================================================
// Types
// ============================================================================

interface CitationsFilters {
  citationNumber: string;
  offenderName: string;
  location: string;
  dateFrom: Date | null;
  dateTo: Date | null;
  status: string;
  officerId: number | null;
  violationType: number | null;
  hasLinkedCall: boolean | null;
}

interface CitationListItem {
  id: number;
  citationNumber: string | null;
  status: string;
  dateOfIssuance: string;
  offenderName: string | null;
  npsdPersonId: number | null;
  violationTypes: number[];
  violationTypeValues: string[];
  propertyLocation: string;
  offenseLocation: string;
  fineAmount: number | null;
  fineToBeDetermined: boolean;
  callCardId: number | null;
  eventId: string | null;
  issuingOfficerName: string;
  createdDate: string;
  modifiedDate: string;
  voidedBy: number | null;
  voidedByName: string | null;
}

interface CitationsScreenProps {
  moduleType: string;
}

// ============================================================================
// Helper Functions
// ============================================================================

function formatDate(date: any): string {
  if (!date) return '';
  return moment(date).format('MM/DD/YYYY');
}

function getViolationList(citation: CitationListItem): string {
  if (citation.violationTypeValues && citation.violationTypeValues.length > 0) {
    return citation.violationTypeValues.join(', ');
  }
  return '';
}

function getFineDisplay(citation: CitationListItem): string {
  if (citation.fineToBeDetermined) return 'TBD';
  if (citation.fineAmount !== null && citation.fineAmount !== undefined) {
    return '$' + citation.fineAmount.toFixed(2);
  }
  return 'TBD';
}

const defaultFilters: CitationsFilters = {
  citationNumber: '',
  offenderName: '',
  location: '',
  dateFrom: null,
  dateTo: null,
  status: '',
  officerId: null,
  violationType: null,
  hasLinkedCall: null,
};

// ============================================================================
// Subcomponents
// ============================================================================

function CitationStatusBadge({ status }: { status: string }) {
  const statusText = (status || 'draft').toUpperCase();
  return <span className={`status-badge ${status || ''}`}>{statusText}</span>;
}

function CitationRow({
  citation,
  moduleType,
  onSelect,
}: {
  citation: CitationListItem;
  moduleType: string;
  onSelect: (citation: CitationListItem) => void;
}) {
  const personLink = `/?sectionName=${moduleType}&tab=Search&function=Open_Person&multiModule=true&params=${citation.npsdPersonId}`;
  const callCardLink = `/?sectionName=${moduleType}&tab=InProgress&function=${moduleType}_CallCard&multiModule=true&params=${citation.callCardId}`;

  return (
    <tr onClick={() => onSelect(citation)}>
      <td>
        <a
          role="button"
          className="citation-number-link"
          onClick={(e) => {
            e.stopPropagation();
            onSelect(citation);
          }}
        >
          {citation.citationNumber || 'DRAFT'}
        </a>
      </td>
      <td>
        <CitationStatusBadge status={citation.status} />
      </td>
      <td>{formatDate(citation.dateOfIssuance)}</td>
      <td>
        {citation.npsdPersonId ? (
          <a
            href={personLink}
            className="cell-link"
            onClick={(e) => e.stopPropagation()}
          >
            {citation.offenderName || '\u2014'}
          </a>
        ) : (
          <span>{citation.offenderName || '\u2014'}</span>
        )}
      </td>
      <td>{getViolationList(citation)}</td>
      <td>{citation.propertyLocation || ''}</td>
      <td>{citation.offenseLocation || ''}</td>
      <td>{getFineDisplay(citation)}</td>
      <td>{formatDate(citation.createdDate)}</td>
      <td>{formatDate(citation.modifiedDate)}</td>
      <td>
        {citation.callCardId ? (
          <a
            href={callCardLink}
            className="cell-link"
            onClick={(e) => e.stopPropagation()}
          >
            {citation.eventId}
          </a>
        ) : (
          <span className="empty-cell">&mdash;</span>
        )}
      </td>
      <td>{citation.issuingOfficerName || ''}</td>
    </tr>
  );
}

function CitationCard({
  citation,
  officerLabel,
  onSelect,
}: {
  citation: CitationListItem;
  moduleType: string;
  officerLabel: string;
  onSelect: (citation: CitationListItem) => void;
}) {
  return (
    <div className="citation-card" onClick={() => onSelect(citation)}>
      <div className="card-header">
        <span className="card-citation-number">
          {citation.citationNumber || 'DRAFT'}
        </span>
        <CitationStatusBadge status={citation.status} />
      </div>
      <div className="card-body">
        <div className="card-row">
          <span className="card-label">Date</span>
          <span className="card-value">
            {formatDate(citation.dateOfIssuance)}
          </span>
        </div>
        <div className="card-row">
          <span className="card-label">Offender</span>
          <span className="card-value">
            {citation.offenderName || '\u2014'}
          </span>
        </div>
        <div className="card-row">
          <span className="card-label">Violation</span>
          <span className="card-value">
            {getViolationList(citation) || '\u2014'}
          </span>
        </div>
        <div className="card-row">
          <span className="card-label">Offense Location</span>
          <span className="card-value">
            {citation.offenseLocation || '\u2014'}
          </span>
        </div>
        <div className="card-row">
          <span className="card-label">Fine</span>
          <span className="card-value">{getFineDisplay(citation)}</span>
        </div>
        <div className="card-row">
          <span className="card-label">{officerLabel}</span>
          <span className="card-value">
            {citation.issuingOfficerName || '\u2014'}
          </span>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// CitationsScreen Component
// ============================================================================

export function CitationsScreen({ moduleType }: CitationsScreenProps) {
  const { theme } = useTheme();

  // --- State ---
  const [isLoading, setIsLoading] = useState(false);
  const [citations, setCitations] = useState<CitationListItem[]>([]);
  const [filters, setFilters] = useState<CitationsFilters>({ ...defaultFilters });
  const [dateFromPickerOpen, setDateFromPickerOpen] = useState(false);
  const [dateToPickerOpen, setDateToPickerOpen] = useState(false);
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [sortField, setSortField] = useState('dateOfIssuance');
  const [sortDir, setSortDir] = useState('desc');
  const [officers, setOfficers] = useState<Array<{ id: number; name: string }>>([]);
  const [violationTypes, setViolationTypes] = useState<Array<{ code: number; value: string }>>([]);

  const officerLabel = useMemo(() => getOfficerLabel(), []);
  const officerPluralLabel = useMemo(() => getOfficerPluralLabel(), []);

  // --- Computed ---
  const startItem = useMemo(() => {
    if (totalCount === 0) return 0;
    return (currentPage - 1) * pageSize + 1;
  }, [currentPage, pageSize, totalCount]);

  const endItem = useMemo(() => {
    const end = currentPage * pageSize;
    return end > totalCount ? totalCount : end;
  }, [currentPage, pageSize, totalCount]);

  const visiblePages = useMemo(() => {
    const pages: number[] = [];
    const start = Math.max(1, currentPage - 2);
    const end = Math.min(totalPages, currentPage + 2);
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  }, [currentPage, totalPages]);

  const statusOptions = useMemo(
    () => [
      { value: '', label: 'All' },
      { value: 'draft', label: 'Draft' },
      { value: 'issued', label: 'Issued' },
      { value: 'voided', label: 'Voided' },
    ],
    []
  );

  const hasLinkedCallOptions = useMemo(
    () => [
      { value: null as boolean | null, label: 'All' },
      { value: true as boolean | null, label: 'Yes' },
      { value: false as boolean | null, label: 'No' },
    ],
    []
  );

  const officerOptions = useMemo(
    () => [{ id: null as number | null, name: `All ${officerPluralLabel}` }, ...officers],
    [officers]
  );

  const violationTypeOptions = useMemo(
    () => [{ code: null as number | null, value: 'All Violations' }, ...violationTypes],
    [violationTypes]
  );

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.citationNumber) count++;
    if (filters.offenderName) count++;
    if (filters.location) count++;
    if (filters.dateFrom) count++;
    if (filters.dateTo) count++;
    if (filters.status) count++;
    if (filters.officerId) count++;
    if (filters.violationType !== null) count++;
    if (filters.hasLinkedCall !== null) count++;
    return count;
  }, [filters]);

  const canCreateCitation = useMemo(
    () => currentUserHasPermission(AccessAuthorities.ROLE_CITATION_CREATE),
    []
  );

  const canExportCitations = useMemo(
    () => currentUserHasPermission(AccessAuthorities.ROLE_CITATION_EXPORT),
    []
  );

  // --- Date picker helpers ---
  const closeDatePickers = useCallback(() => {
    setDateFromPickerOpen(false);
    setDateToPickerOpen(false);
  }, []);

  const openDateFromPicker = useCallback(() => {
    setDateToPickerOpen(false);
    setDateFromPickerOpen(true);
  }, []);

  const openDateToPicker = useCallback(() => {
    setDateFromPickerOpen(false);
    setDateToPickerOpen(true);
  }, []);

  // --- Load functions ---
  const loadCitations = useCallback(
    (
      pageOverride?: number,
      pageSizeOverride?: number,
      sortFieldOverride?: string,
      sortDirOverride?: string,
      filtersOverride?: CitationsFilters
    ) => {
      setIsLoading(true);

      const f = filtersOverride ?? filters;
      const curPage = pageOverride ?? currentPage;
      const curPageSize = pageSizeOverride ?? pageSize;
      const curSortField = sortFieldOverride ?? sortField;
      const curSortDir = sortDirOverride ?? sortDir;

      const apiFilters: any = {
        sortField: curSortField,
        sortDir: curSortDir,
      };

      if (f.citationNumber) apiFilters.citationNumber = f.citationNumber;
      if (f.offenderName) apiFilters.offenderName = f.offenderName;
      if (f.location) apiFilters.location = f.location;
      if (f.dateFrom) apiFilters.dateFrom = moment(f.dateFrom).format('YYYY-MM-DD');
      if (f.dateTo) apiFilters.dateTo = moment(f.dateTo).format('YYYY-MM-DD');
      if (f.status) apiFilters.status = f.status;
      if (f.officerId) apiFilters.officerId = f.officerId;
      if (f.violationType !== null) apiFilters.violationType = f.violationType;
      if (f.hasLinkedCall !== null) apiFilters.hasLinkedCall = f.hasLinkedCall;

      const start = (curPage - 1) * curPageSize;

      api.searchCitations(
        apiFilters,
        start,
        curPageSize,
        (result: any) => {
          setCitations(result.rows || []);
          setTotalCount(result.records || 0);
          setTotalPages(result.numPages || 1);
          setIsLoading(false);
        },
        (_error: any) => {
          App.showPopupNotification('Failed to load citations.', 'error');
          setIsLoading(false);
        }
      );
    },
    [filters, currentPage, pageSize, sortField, sortDir]
  );

  const loadOfficers = useCallback(() => {
    const modType = getModuleType();
    if (modType === 'cad') {
      api.getCallCardSearchOfficers((result: any) => {
        if (result && result.officerNames) {
          setOfficers(
            result.officerNames.sort((a: any, b: any) =>
              a.name > b.name ? 1 : -1
            )
          );
        }
      });
    } else if (modType === 'mdt') {
      api.getReportSearchOfficers((result: any) => {
        if (result && result.officerNames) {
          setOfficers(
            result.officerNames.sort((a: any, b: any) =>
              a.name > b.name ? 1 : -1
            )
          );
        }
      });
    }
  }, []);

  const loadViolationTypes = useCallback(() => {
    api.getViolationTypes(
      (result: any) => {
        if (result && Array.isArray(result)) {
          setViolationTypes(
            result.sort(
              (a: any, b: any) => (a.listIdx ?? 0) - (b.listIdx ?? 0)
            )
          );
        }
      },
      (error: any) => {
        console.warn('Failed to load violation types from API:', error);
        App.showPopupNotification('Failed to load violation types.', 'error');
      }
    );
  }, []);

  // --- Mount ---
  useEffect(() => {
    loadCitations();
    loadOfficers();
    loadViolationTypes();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Handlers ---
  const searchCitations = useCallback(() => {
    closeDatePickers();
    setCurrentPage(1);
    loadCitations(1);
  }, [closeDatePickers, loadCitations]);

  const clearFilters = useCallback(() => {
    closeDatePickers();
    const cleared = { ...defaultFilters };
    setFilters(cleared);
    setCurrentPage(1);
    loadCitations(1, undefined, undefined, undefined, cleared);
  }, [closeDatePickers, loadCitations]);

  const sortBy = useCallback(
    (field: string) => {
      let newDir: string;
      let newField: string;
      if (sortField === field) {
        newDir = sortDir === 'asc' ? 'desc' : 'asc';
        newField = field;
      } else {
        newField = field;
        newDir = 'asc';
      }
      setSortField(newField);
      setSortDir(newDir);
      loadCitations(undefined, undefined, newField, newDir);
    },
    [sortField, sortDir, loadCitations]
  );

  const goToPage = useCallback(
    (p: number) => {
      if (p < 1 || p > totalPages) return;
      setCurrentPage(p);
      loadCitations(p);
    },
    [totalPages, loadCitations]
  );

  const toggleFilters = useCallback(() => {
    setFiltersExpanded((prev) => {
      if (prev) {
        closeDatePickers();
      }
      return !prev;
    });
  }, [closeDatePickers]);

  const onStatusChange = useCallback(
    (value: string | number | null) => {
      closeDatePickers();
      const newFilters = { ...filters, status: (value as string) ?? '' };
      setFilters(newFilters);
      setCurrentPage(1);
      loadCitations(1, undefined, undefined, undefined, newFilters);
    },
    [closeDatePickers, filters, loadCitations]
  );

  const onHasLinkedCallChange = useCallback(
    (value: string | number | null) => {
      closeDatePickers();
      const newFilters = { ...filters, hasLinkedCall: value as unknown as boolean | null };
      setFilters(newFilters);
      setCurrentPage(1);
      loadCitations(1, undefined, undefined, undefined, newFilters);
    },
    [closeDatePickers, filters, loadCitations]
  );

  const onOfficerChange = useCallback(
    (value: string | number | null) => {
      closeDatePickers();
      const newFilters = { ...filters, officerId: value as number | null };
      setFilters(newFilters);
      setCurrentPage(1);
      loadCitations(1, undefined, undefined, undefined, newFilters);
    },
    [closeDatePickers, filters, loadCitations]
  );

  const onViolationTypeChange = useCallback(
    (value: string | number | null) => {
      closeDatePickers();
      const newFilters = { ...filters, violationType: value as number | null };
      setFilters(newFilters);
      setCurrentPage(1);
      loadCitations(1, undefined, undefined, undefined, newFilters);
    },
    [closeDatePickers, filters, loadCitations]
  );

  const onDateFromChange = useCallback(
    (value: Date | null) => {
      const newFilters = { ...filters, dateFrom: value };
      setFilters(newFilters);
    },
    [filters]
  );

  const onDateToChange = useCallback(
    (value: Date | null) => {
      const newFilters = { ...filters, dateTo: value };
      setFilters(newFilters);
    },
    [filters]
  );

  const onPageSizeChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const newSize = parseInt(e.target.value, 10);
      setPageSize(newSize);
      setCurrentPage(1);
      loadCitations(1, newSize);
    },
    [loadCitations]
  );

  const openNewCitation = useCallback(() => {
    openNewCitationFromCitationsScreen();
  }, []);

  const viewCitation = useCallback((citation: CitationListItem) => {
    viewCitationFromGrid(citation.id);
  }, []);

  const handleExport = useCallback(() => {
    exportCitationsToCSV(filters, sortField, sortDir);
  }, [filters, sortField, sortDir]);

  const updateFilterField = useCallback(
    (field: keyof CitationsFilters, value: any) => {
      setFilters((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  // --- Render ---
  return (
    <div className={`citations-screen ${theme}`.trim()}>
      {/* Header */}
      <div className="header">
        <h1>Citations</h1>
        <div className="header-actions">
          {canCreateCitation && (
            <button className="btn-new" onClick={openNewCitation}>
              <i className="fa fa-plus"></i> New Citation
            </button>
          )}
          {canExportCitations && (
            <button className="btn-export" onClick={handleExport}>
              <i className="fa fa-download"></i> Export
            </button>
          )}
        </div>
      </div>

      {/* Mobile Filters Toggle Bar */}
      <div className="mobile-filters-bar">
        <button className="btn-filters-toggle" onClick={toggleFilters}>
          <i className="fa fa-filter"></i>
          Filters
          {activeFilterCount > 0 && (
            <span className="filter-count-badge">{activeFilterCount}</span>
          )}
          <i
            className={`fa ${filtersExpanded ? 'fa-chevron-up' : 'fa-chevron-down'}`}
          ></i>
        </button>
        <button className="btn-search-mobile" onClick={searchCitations}>
          <i className="fa fa-search"></i>
        </button>
      </div>

      {/* Filters */}
      <div
        className={`filters-section${filtersExpanded ? ' filters-expanded' : ''}`}
      >
        <div className="filters-row">
          <div className="filter-group">
            <label>Citation #</label>
            <input
              type="text"
              className="filter-input"
              value={filters.citationNumber}
              placeholder="Enter Citation #"
              onChange={(e) =>
                updateFilterField('citationNumber', e.target.value)
              }
              onKeyUp={(e) => {
                if (e.key === 'Enter') searchCitations();
              }}
              onFocus={closeDatePickers}
            />
          </div>
          <div className="filter-group">
            <label>Offender Name</label>
            <input
              type="text"
              className="filter-input"
              value={filters.offenderName}
              placeholder="Enter Name"
              onChange={(e) =>
                updateFilterField('offenderName', e.target.value)
              }
              onKeyUp={(e) => {
                if (e.key === 'Enter') searchCitations();
              }}
              onFocus={closeDatePickers}
            />
          </div>
          <div className="filter-group">
            <label>Location</label>
            <input
              type="text"
              className="filter-input"
              value={filters.location}
              placeholder="Enter Location"
              onChange={(e) => updateFilterField('location', e.target.value)}
              onKeyUp={(e) => {
                if (e.key === 'Enter') searchCitations();
              }}
              onFocus={closeDatePickers}
            />
          </div>
        </div>
        <div className="filters-row">
          <div className="filter-group">
            <label>From Date</label>
            <DateTimePicker
              value={filters.dateFrom}
              type="date"
              format="DD MMM YYYY"
              valueType="date"
              clearable={true}
              appendToBody={true}
              popupClass={theme || ''}
              open={dateFromPickerOpen}
              onFocus={openDateFromPicker}
              onChange={onDateFromChange}
              onOpenChange={(open) => setDateFromPickerOpen(open)}
            />
          </div>
          <div className="filter-group">
            <label>To Date</label>
            <DateTimePicker
              value={filters.dateTo}
              type="date"
              format="DD MMM YYYY"
              valueType="date"
              clearable={true}
              appendToBody={true}
              popupClass={theme || ''}
              open={dateToPickerOpen}
              onFocus={openDateToPicker}
              onChange={onDateToChange}
              onOpenChange={(open) => setDateToPickerOpen(open)}
            />
          </div>
          <div className="filter-group" onClick={closeDatePickers}>
            <label>Status</label>
            <Dropdownlist
              value={filters.status}
              items={statusOptions}
              labelKey="label"
              valueKey="value"
              placeholder="All"
              searchable={false}
              onChange={onStatusChange}
            />
          </div>
          <div className="filter-group" onClick={closeDatePickers}>
            <label>Has Linked Call</label>
            <Dropdownlist
              value={filters.hasLinkedCall}
              items={hasLinkedCallOptions}
              labelKey="label"
              valueKey="value"
              placeholder="All"
              searchable={false}
              onChange={onHasLinkedCallChange}
            />
          </div>
        </div>
        <div className="filters-row">
          <div className="filter-group" onClick={closeDatePickers}>
            <label>{officerLabel}</label>
            <Dropdownlist
              value={filters.officerId}
              items={officerOptions}
              labelKey="name"
              valueKey="id"
              placeholder={`All ${officerPluralLabel}`}
              searchable={true}
              onChange={onOfficerChange}
            />
          </div>
          <div className="filter-group" onClick={closeDatePickers}>
            <label>Violation Type</label>
            <Dropdownlist
              value={filters.violationType}
              items={violationTypeOptions}
              labelKey="value"
              valueKey="code"
              placeholder="All Violations"
              searchable={true}
              sortItems={false}
              onChange={onViolationTypeChange}
            />
          </div>
          <div className="filter-buttons">
            <button className="btn-search" onClick={searchCitations}>
              Search
            </button>
            <button className="btn-clear" onClick={clearFilters}>
              Clear
            </button>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <div>Loading citations...</div>
        </div>
      )}

      {/* Desktop Table */}
      {!isLoading && citations.length > 0 && (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th className="sortable" onClick={() => sortBy('citationNumber')}>
                  Citation #
                  {sortField === 'citationNumber' && (
                    <span>{sortDir === 'asc' ? '\u25B2' : '\u25BC'}</span>
                  )}
                </th>
                <th className="sortable" onClick={() => sortBy('status')}>
                  Status
                  {sortField === 'status' && (
                    <span>{sortDir === 'asc' ? '\u25B2' : '\u25BC'}</span>
                  )}
                </th>
                <th
                  className="sortable"
                  onClick={() => sortBy('dateOfIssuance')}
                >
                  Date
                  {sortField === 'dateOfIssuance' && (
                    <span>{sortDir === 'asc' ? '\u25B2' : '\u25BC'}</span>
                  )}
                </th>
                <th>Offender</th>
                <th>Violation(s)</th>
                <th
                  className="sortable"
                  onClick={() => sortBy('propertyLocation')}
                >
                  Property Location
                  {sortField === 'propertyLocation' && (
                    <span>{sortDir === 'asc' ? '\u25B2' : '\u25BC'}</span>
                  )}
                </th>
                <th
                  className="sortable"
                  onClick={() => sortBy('offenseLocation')}
                >
                  Offense Location
                  {sortField === 'offenseLocation' && (
                    <span>{sortDir === 'asc' ? '\u25B2' : '\u25BC'}</span>
                  )}
                </th>
                <th className="sortable" onClick={() => sortBy('fineAmount')}>
                  Fine
                  {sortField === 'fineAmount' && (
                    <span>{sortDir === 'asc' ? '\u25B2' : '\u25BC'}</span>
                  )}
                </th>
                <th className="sortable" onClick={() => sortBy('dateCreated')}>
                  Created
                  {sortField === 'dateCreated' && (
                    <span>{sortDir === 'asc' ? '\u25B2' : '\u25BC'}</span>
                  )}
                </th>
                <th className="sortable" onClick={() => sortBy('lastUpdated')}>
                  Modified
                  {sortField === 'lastUpdated' && (
                    <span>{sortDir === 'asc' ? '\u25B2' : '\u25BC'}</span>
                  )}
                </th>
                <th>Call</th>
                <th>{officerLabel}</th>
              </tr>
            </thead>
            <tbody>
              {citations.map((citation) => (
                <CitationRow
                  key={citation.id}
                  citation={citation}
                  moduleType={moduleType}
                  onSelect={viewCitation}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Mobile Cards */}
      {!isLoading && citations.length > 0 && (
        <div className="mobile-cards">
          {citations.map((citation) => (
            <CitationCard
              key={'card-' + citation.id}
              citation={citation}
              moduleType={moduleType}
              officerLabel={officerLabel}
              onSelect={viewCitation}
            />
          ))}
        </div>
      )}

      {/* Empty State */}
      {!isLoading && citations.length === 0 && (
        <div className="empty-state">No citations found.</div>
      )}

      {/* Pagination */}
      {!isLoading && totalPages > 0 && (
        <div className="pagination-container">
          <div className="pagination-info">
            Showing {startItem} - {endItem} of {totalCount} citations
          </div>
          <div className="pagination-controls">
            <button
              className="pagination-btn"
              disabled={currentPage === 1}
              onClick={() => goToPage(1)}
            >
              First
            </button>
            <button
              className="pagination-btn"
              disabled={currentPage === 1}
              onClick={() => goToPage(currentPage - 1)}
            >
              Prev
            </button>
            {visiblePages.map((p) => (
              <button
                key={p}
                className={`pagination-btn${p === currentPage ? ' active' : ''}`}
                onClick={() => goToPage(p)}
              >
                {p}
              </button>
            ))}
            <button
              className="pagination-btn"
              disabled={currentPage === totalPages}
              onClick={() => goToPage(currentPage + 1)}
            >
              Next
            </button>
            <button
              className="pagination-btn"
              disabled={currentPage === totalPages}
              onClick={() => goToPage(totalPages)}
            >
              Last
            </button>
            <select
              className="page-size-select"
              value={pageSize}
              onChange={onPageSizeChange}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Entry Point Functions
// ============================================================================

function openNewCitationFromCitationsScreen() {
  citationParentContext = 'citations';
  citationParentId = null;

  navigateToNewCitation();
}

function viewCitationFromGrid(citationId: number) {
  citationParentContext = 'citations';
  citationParentId = null;

  navigateToCitation(citationId, 'view');
}

// ============================================================================
// Export
// ============================================================================

function exportCitationsToCSV(
  filters: CitationsFilters,
  sortField?: string,
  sortDir?: string
) {
  // Date range is required for export
  if (!filters.dateFrom || !filters.dateTo) {
    App.showPopupNotification(
      'Please select a date range for export.',
      'warning'
    );
    return;
  }

  // Validate date range does not exceed 365 days
  const dateFrom = moment(filters.dateFrom);
  const dateTo = moment(filters.dateTo);
  const daysDiff = dateTo.diff(dateFrom, 'days');
  if (daysDiff > 365) {
    App.showPopupNotification('Date range cannot exceed 365 days.', 'warning');
    return;
  }
  if (daysDiff < 0) {
    App.showPopupNotification('End date must be after start date.', 'warning');
    return;
  }

  const exportFilters: any = {
    dateFrom: moment(filters.dateFrom).format('YYYY-MM-DD'),
    dateTo: moment(filters.dateTo).format('YYYY-MM-DD'),
  };

  if (filters.citationNumber)
    exportFilters.citationNumber = filters.citationNumber;
  if (filters.offenderName) exportFilters.offenderName = filters.offenderName;
  if (filters.location) exportFilters.location = filters.location;
  if (filters.status) exportFilters.status = filters.status;
  if (filters.officerId) exportFilters.officerId = filters.officerId;
  if (filters.violationType !== null)
    exportFilters.violationType = filters.violationType;
  if (sortField) exportFilters.sortField = sortField;
  if (sortDir) exportFilters.sortDir = sortDir;

  api.exportCitations(
    exportFilters,
    (result: any) => {
      if (result.header && result.records) {
        // Build CSV content
        const csvRows: string[] = [];
        csvRows.push(result.header.join(','));
        result.records.forEach((row: any[]) => {
          csvRows.push(
            row
              .map((cell) => {
                const str = String(cell ?? '');
                // Escape quotes and wrap in quotes if contains comma or newline
                if (
                  str.includes(',') ||
                  str.includes('\n') ||
                  str.includes('"')
                ) {
                  return '"' + str.replace(/"/g, '""') + '"';
                }
                return str;
              })
              .join(',')
          );
        });
        const csvContent = csvRows.join('\n');

        // Create and download file
        const blob = new Blob([csvContent], {
          type: 'text/csv;charset=utf-8;',
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `citations-${moment().format('YYYY-MM-DD')}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } else {
        App.showPopupNotification('Export failed: invalid response.', 'error');
      }
    },
    (_error: any) => {
      App.showPopupNotification('Failed to export citations.', 'error');
    }
  );
}

